








































































































































import {
  Component,
  Vue,
  Prop,
} from "@/shared/vuepack";
import Button from "@/shared/components/ui/Button.vue";
import {
  Communication,
  PartnerEmployerList,
} from "@/employer/types";
import { Validator } from "vee-validate";
import moment from "moment";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import CommunicationEmployerGroups from "./CommunicationEmployerGroups.vue";
import ImageUpload from "@/employer/components/communications/ImageUpload.vue";
import {
  CommunicationClient,
  CommunicationPortal,
  Permission,
  HsabPlanType,
  CommunicationAccount,
} from "@/shared/types";
import { CommunicationIcons } from "@/employer/components/shared/CommunicationIcons";
import { BankCustodian } from "@/admin/types";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { usePartnerStore } from "@/employer/store/partner";
import { useCommunicationStore } from "@/shared/store/communication";

@Component({
  components:
  {
    Button,
    GenericLoadingSpinner,
    ImageUpload,
    CommunicationEmployerGroups
  }
})
export default class AddCommunication extends Vue {
  notifierStore = useNotifierStore();
  partnerStore = usePartnerStore();
  userStore = useUserStore();
  rolePermissionsStore = useRolePermissionsStore();
  communicationStore = useCommunicationStore();

  @Prop()
  cancelCommunication: () => void;

  @Prop()
  communication: Communication;

  @Prop()
  onSave: () => void;

  @Prop()
  partnerId: number;

  @Prop()
  portal: CommunicationPortal;

  $validator: Validator;
  isSubmitting = false;
  selectedImage = null;
  isImageDeleted = false;
  selectedIcon = "";
  employerIds = [];
  model = this.createDefaultCommunication();
  parser = new DOMParser();

  createDefaultCommunication (): Communication {
    return {
      id: null,
      startDate: "",
      endDate: "",
      displayOrder: 1,
      subject: "",
      message: "",
      actionText: "",
      actionLink: "",
      isActionLinkExternal: false,
      employerGroups: [],
      displayToIndividuals: false,
      isEmployerExclusionList: false,
      applyToAllEmployers: true,
      imageName: "",
      portal: this.portal,
      iconClass: "",
      accountType: HsabPlanType.Unknown,
      communicationAccount: CommunicationAccount.hsa,
      client: CommunicationClient.All
    };
  }

  beforeMount () {
    this.$validator.extend("positive", {
      getMessage: _ => "Display order must be a positive number",
      validate: _ => _ > 0
    });
    this.$validator.extend("dateLess", {
      getMessage: _ => "Start date must occur before end date.",
      validate: _ => {
        if (!this.model.endDate) {
          return true;
        }
        const result = moment(this.model.startDate) < moment(this.model.endDate);
        if (!result) {
          this.model.endDate = "";
        } else {
          this.$validator.reset();
        }

        return result;
      }
    });
    this.$validator.extend("dateMore", {
      getMessage: _ => "End date must occur after start date.",
      validate: _ => {
        const result = moment(this.model.startDate) < moment(this.model.endDate);
        if (!result) {
          this.model.startDate = "";
        } else {
          this.$validator.reset();
        }

        return result;
      }
    });
  }

  mounted () {
    this.communicationStore.loadPartnerEmployers(this.partnerId);
    if (this.communication) {
      this.model.id = this.communication.id;
      this.model.startDate = this.communication.startDate;
      this.model.endDate = this.communication.endDate;
      this.model.subject = this.communication.subject;
      this.model.message = this.communication.message;
      this.model.displayOrder = this.communication.displayOrder;
      this.model.displayToIndividuals = this.communication.displayToIndividuals;
      this.model.isEmployerExclusionList = this.communication.isEmployerExclusionList;
      this.model.applyToAllEmployers = this.communication.applyToAllEmployers;
      this.model.actionText = this.communication.actionText;
      this.model.actionLink = this.communication.actionLink;
      this.model.isActionLinkExternal = this.communication.isActionLinkExternal;
      this.model.imageName = this.communication.imageName;
      if (this.communication.employerGroups) {
        this.model.employerGroups = [...this.communication.employerGroups];
        this.employerIds = this.model.employerGroups.map(t => t.id);
      }
      this.model.iconClass = this.communication.iconClass;
      this.model.client = this.communication.client;
      this.model.communicationAccount = this.communication.communicationAccount;
      this.model.accountType = this.communication.accountType;
      this.selectedIcon = this.communication.iconClass;
    }
  }

  get isForEmployeePortal () {
    return this.portal === CommunicationPortal.Employee;
  }

  get isPartner () {
    return !!this.partnerId;
  }

  get tooManyEmployers () {
    return this.communicationStore.partnerEmployerList
      && this.communicationStore.partnerEmployerList.tooManyEmployers;
  }

  get hideMultiSelect () {
    return this.model.applyToAllEmployers;
  }

  get partnerEmployers () {
    return this.communicationStore.partnerEmployerList.employers.map(e => ({
      text: e.name,
      value: e.id
    }));
  }

  get hasWidgetWritePermission () {
    return this.rolePermissionsStore.userHasPartnerPermission(Permission.PartnerCommunicationWidgetWrite, this.partnerId);
  }

  get saveDisabled () {
    return !this.hasWidgetWritePermission
      || this.$validator.errors.any() || this.isSubmitting;
  }

  get commIcons () {
    return CommunicationIcons;
  }

  get planTypeOptions () {
    return [
      { value: HsabPlanType.Unknown, text: "All Non-HSA" },
      { value: HsabPlanType.MedicalFlex, text: "FSA" },
      { value: HsabPlanType.DependentCare, text: "Dependent Care" },
      { value: HsabPlanType.HealthReimbursementAccount, text: "HRA" },
      { value: HsabPlanType.CommuterParking, text: "Commuter Parking" },
      { value: HsabPlanType.CommuterTransit, text: "Commuter Transit" },
    ];
  }

  get clientOptions () {
    return [
      { value: "all", text: "All" },
      { value: "web", text: "Web" },
      { value: "mobile", text: "Mobile" }
    ];
  }

  get isHsaBank () {
    return this.userStore.currentPartner.custodian === BankCustodian.HsaBank;
  }

  get commAccountOptions () {
    return [
      { value: CommunicationAccount.hsa, text: "HSA" },
      { value: CommunicationAccount.nonHsa, text: "Non-HSA Plans" },
      { value: CommunicationAccount.bothHsaAndNonHsa, text: "All Plan Types" }
    ];
  }

  get isNotional () {
    return this.model.communicationAccount === CommunicationAccount.nonHsa;
  }

  selectIcon (e) {
    this.model.iconClass = e.target.value;
  }

  handleMultiSelectInput (values) {
    this.employerIds = [...values];
    const filteredList = this.communicationStore.partnerEmployerList.employers
      .filter(t => values.includes(t.id))
      .map(eg => ({ id: eg.id, name: eg.name, code: null }));
    this.model.employerGroups = [...filteredList];
  }

  save (isPreview: boolean) {
    this.$validator.validateAll()
      .then(isValid => {
        if (!isValid) {
          return;
        }

        this.isSubmitting = true;
        if (this.model.communicationAccount !== CommunicationAccount.nonHsa) {
          this.model.accountType = HsabPlanType.Unknown;
        }
        this.partnerStore.postPartnerCommunication({ partnerId: this.partnerId, portal: this.portal, communication: this.model })
          .then(_ => {
            if (_ && _.success) {
              if (!this.model.id) {
                this.model.id = _.id;
                this.userStore.addCommunication({ portal: this.portal, value: this.model });
              } else if (this.communication) {
                this.communication.startDate = this.model.startDate;
                this.communication.endDate = this.model.endDate;
                this.communication.subject = this.model.subject;
                this.communication.displayOrder = this.model.displayOrder;
                this.communication.message = this.model.message;
                this.communication.displayToIndividuals = this.model.displayToIndividuals;
                this.communication.isEmployerExclusionList = this.model.isEmployerExclusionList;
                this.communication.applyToAllEmployers = this.model.applyToAllEmployers;
                this.communication.actionText = this.model.actionText;
                this.communication.actionLink = this.model.actionLink;
                this.communication.isActionLinkExternal = this.model.isActionLinkExternal;
                this.communication.imageName = this.model.imageName;
                if (this.model.employerGroups) {
                  this.communication.employerGroups = [...this.model.employerGroups];
                }
                this.communication.iconClass = this.model.iconClass;
                this.communication.accountType = this.model.accountType;
                this.communication.communicationAccount = this.model.communicationAccount;
                this.communication.client = this.model.client;
              }
              this.partnerStore.saveCommunicationImage(
                {
                  partnerId: this.partnerId,
                  communicationId: this.model.id,
                  imageFile: this.selectedImage,
                  isImageDeleted: this.isImageDeleted
                }).then(() => {
                  this.notifierStore.setSuccess("Communication saved");
                  if (!isPreview) {
                    this.model = this.createDefaultCommunication();
                    this.$validator.reset();
                    this.onSave();
                  }
                  else {
                    this.communicationStore.loadPreviewCommunication(this.model.id);
                  }
                });
            } else {
              this.notifierStore.setDanger("Failed to save communication");
            }
          })
          .finally(() => this.isSubmitting = false);
      });
  }

  onImageSelected (file) {
    this.selectedImage = file;
    this.model.imageName = file.name;
    this.isImageDeleted = false;
  }

  createDownloadLink () {
    this.partnerStore.imageGetLink({ partnerId: this.partnerId, communicationId: this.communication.id });
  }

  removeImage () {
    this.isImageDeleted = this.communication.imageName !== null;
    this.selectedImage = null;
    this.model.imageName = null;
  }

  commAccountChange () {
    alert(this.model.communicationAccount);
    if (this.model.communicationAccount !== CommunicationAccount.nonHsa) {
      this.model.accountType = HsabPlanType.Unknown;
    }
  }
}
